import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface LegalPageProps {
  title: string;
  lastUpdated: string;
  intro?: string;
  children: React.ReactNode;
}

const legalLinks = [
  { label: 'Terms of Service', path: '/terms' },
  { label: 'Privacy Policy', path: '/privacy' },
  { label: 'Security', path: '/security' },
];

export const LegalSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="mb-8">
    <h2 className="text-xl font-semibold text-white mb-3">{title}</h2>
    <div className="space-y-3 text-gray-300 leading-relaxed">{children}</div>
  </section>
);

export const LegalStatement: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="border-l-2 border-[#22d3ee]/60 bg-white/5 rounded-r-lg px-4 py-3 text-gray-200">
    {children}
  </p>
);

export const LegalList: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul className="space-y-2">
    {items.map((item, i) => (
      <li key={i} className="flex items-start">
        <span className="text-[#22d3ee] mr-3 mt-1">•</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const LegalPage: React.FC<LegalPageProps> = ({ title, lastUpdated, intro, children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors text-sm mb-8"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <h1 className="text-4xl font-bold text-white mb-2">{title}</h1>
        <p className="text-sm text-gray-400 mb-8">Last updated: {lastUpdated}</p>

        {intro && <p className="text-gray-300 leading-relaxed mb-10">{intro}</p>}

        {children}

        {/* Cross-links to the other legal pages */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {legalLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => navigate(link.path)}
                disabled={location.pathname === link.path}
                className={`text-sm transition-colors ${
                  location.pathname === link.path
                    ? 'text-white font-medium cursor-default'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              Home
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            © 2025 BlockBoard. BlockBoard is a software platform and is not a property manager,
            financial institution, law firm, tenant screening service, or compliance provider.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
